import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { StorageService } from '../services/storage.service';
import { Router } from '@angular/router';
import { catchError, Observable, Subject, switchMap, tap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { RefreshTokenResponseI } from '../models/accounts.model';
import { CrashMonitorService } from '../services/crash-monitor.service';

const _refreshSubject = new Subject<any>();

export const tokenInterceptorInterceptor: HttpInterceptorFn = (req, next) => {
  const storageService = inject(StorageService);
  const router = inject(Router);
  const authService = inject(AuthService);
  const crashMonitor = inject(CrashMonitorService);

  const token = storageService.get('userToken');

  if (token) {
    req = req.clone({
      setHeaders: {
        Authorization: `${token}`
      }
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      crashMonitor.logApiError(error, req.url);
      if (error.status === 429 || error.status === 403) {
        storageService.clearAll();
        router.navigate(['/login']);
      }
      else if (error.status == 401) {
        return _handle401Error(req, next, storageService, authService, router);
      }

      return throwError(() => error);
    })
  );
};

const _handle401Error = (req: HttpRequest<unknown>, next: { (req: HttpRequest<unknown>): Observable<HttpEvent<unknown>>; (arg0: any): any; }, storageService: StorageService, authService: AuthService, router: Router) => {

  if (_refreshSubject.observers.length === 0) {
    const tokenData = {
      accessToken: storageService.get('userToken')?.replace('Bearer ', ''),
      refreshToken: storageService.get('refreshToken')
    };

    authService.getRefreshToken(tokenData).subscribe({
      next: (data: RefreshTokenResponseI) => {
        storageService.remove('userToken');
        storageService.remove('refreshToken');
        storageService.add("userToken", data.accessToken);
        storageService.add("refreshToken", data.refreshToken);

        _refreshSubject.next(data);
        _refreshSubject.complete();
      },
      error: () => {
        storageService.clearAll();
        router.navigate(['/login']);
        _refreshSubject.error('Token refresh failed');
      }
    });
  }

  return _refreshSubject.pipe(
    switchMap(() => {
      const newToken = storageService.get('userToken');
      const clonedReq = req.clone({
        setHeaders: {
          Authorization: `${newToken}`
        }
      });
      return next(clonedReq);
    }),
    catchError(() => {
      storageService.clearAll();
      router.navigate(['/login']);
      return throwError(() => new Error('Unauthorized'));
    })
  );
};
